import type { BridgePolicyStatus } from '@gh/common/models'
import { bridgePolicyStatusSchema } from '@gh/common/models'
import { readBridgePublishableConfig, type BridgePublishableConfig } from './bridgeConfig'
import { withBridgeTransportRetry } from './bridgeCallableRetry'

export class BridgePublishableClientError extends Error {
  constructor(
    message: string,
    readonly statusCode?: number,
  ) {
    super(message)
    this.name = 'BridgePublishableClientError'
  }
}

export type BridgePayerSearchRequest = {
  query: string
  limit?: number
}

export type BridgePayerSearchHit = {
  id?: string
  name?: string
  code?: string
  hint?: string | null
  memberId?: boolean
  [key: string]: unknown
}

export type BridgePayerSearchResponse = {
  items?: BridgePayerSearchHit[]
  [key: string]: unknown
}

export type BridgeProviderEligibilityRequest = {
  dateOfService: string
  serviceTypeId: string
  location: { state: string; country?: string }
  payerId: string
}

export type BridgeSoftEligibleProvider = {
  id: string
  name: string
  npi?: string
  externalId?: string
  type?: string
}

export type BridgeProviderEligibilityResponse = {
  id?: string
  status?: string
  eligible?: boolean
  validatedAt?: string
  payerId?: string
  serviceTypeId?: string
  providers?: BridgeSoftEligibleProvider[]
  [key: string]: unknown
}

export type BridgePublishableHttp = { fetch: typeof fetch }

/** Bare `fetch` must not be passed unbound — browsers require Window as receiver. */
const defaultBoundFetch: typeof fetch = (input, init) => globalThis.fetch(input, init)

function defaultHttp(): BridgePublishableHttp {
  return { fetch: defaultBoundFetch }
}

function bridgeAuthHeaders(apiKey: string, method?: string): HeadersInit {
  return {
    'X-API-Key': apiKey,
    Accept: 'application/json',
    ...(method !== 'GET' ? { 'Content-Type': 'application/json' } : {}),
  }
}

function bridgeErrorMessage(body: unknown, statusText: string, status: number): string {
  if (typeof body === 'object' && body !== null) {
    const record = body as Record<string, unknown>
    if (typeof record.message === 'string' && record.message.length > 0) {
      return record.message
    }
    if (typeof record.error === 'string' && record.error.length > 0) {
      return record.error
    }
    if (typeof record.type === 'string' && record.type.length > 0) {
      return record.type
    }
  }
  return statusText || `Bridge request failed (${status})`
}

async function bridgeJsonRequest<T>(
  config: BridgePublishableConfig,
  path: string,
  init: RequestInit,
  http: BridgePublishableHttp = defaultHttp(),
): Promise<T> {
  const response = await http.fetch(`${config.baseUrl}${path}`, {
    ...init,
    headers: {
      ...bridgeAuthHeaders(config.apiKey, init.method),
      ...(init.headers ?? {}),
    },
  })

  const text = await response.text()
  let body: unknown
  try {
    body = text ? (JSON.parse(text) as unknown) : undefined
  } catch {
    body = { message: text || response.statusText }
  }

  if (!response.ok) {
    throw new BridgePublishableClientError(
      bridgeErrorMessage(body, response.statusText, response.status),
      response.status,
    )
  }

  return body as T
}

export function createBridgePublishableClient(options?: {
  config?: BridgePublishableConfig | null
  http?: BridgePublishableHttp
}) {
  const http = options?.http ?? defaultHttp()

  function configOrThrow(): BridgePublishableConfig {
    const config = options?.config ?? readBridgePublishableConfig()
    if (!config) {
      throw new BridgePublishableClientError(
        'Bridge publishable key is not configured (VITE_BRIDGE_PUBLISHABLE_KEY)',
      )
    }
    return config
  }

  return {
    async searchPayers(
      request: BridgePayerSearchRequest,
      options?: { signal?: AbortSignal },
    ): Promise<BridgePayerSearchResponse> {
      const config = configOrThrow()
      const limit = request.limit ?? 20
      const signal = options?.signal
      return withBridgeTransportRetry(
        () =>
          bridgeJsonRequest<BridgePayerSearchResponse>(
            config,
            '/api/search/payers',
            {
              method: 'POST',
              body: JSON.stringify({ query: request.query, limit }),
              signal,
            },
            http,
          ),
        { signal },
      )
    },

    async checkProviderEligibility(
      request: BridgeProviderEligibilityRequest,
      options?: { signal?: AbortSignal },
    ): Promise<BridgeProviderEligibilityResponse> {
      const config = configOrThrow()
      const signal = options?.signal
      return withBridgeTransportRetry(
        () =>
          bridgeJsonRequest<BridgeProviderEligibilityResponse>(
            config,
            '/api/provider-eligibility',
            {
              method: 'POST',
              body: JSON.stringify(request),
              signal,
            },
            http,
          ),
        { signal },
      )
    },
  }
}

export type BridgePublishableClient = ReturnType<typeof createBridgePublishableClient>

export function parseBridgePolicyStatus(status: string | undefined): BridgePolicyStatus {
  const parsed = bridgePolicyStatusSchema.safeParse(status)
  return parsed.success ? parsed.data : 'UNKNOWN'
}

/**
 * Bridge statuses that are done resolving (backend merge + patient link when CONFIRMED).
 *
 * v1: treat UNKNOWN as terminal (failed policy). Bridge docs list UNKNOWN in the
 * status enum without defining it, and async guidance says leave PENDING = result.
 * Revisit later if UNKNOWN turns out to be transitional / inconclusive rather than final.
 */
export function isBridgePolicyTerminalStatus(status: BridgePolicyStatus): boolean {
  return status !== 'PENDING' && status !== 'REVALIDATING'
}

export function buildProviderEligibilityRequest(args: {
  payerId: string
  state: string
  serviceTypeId: string
  dateOfService?: Date
}): BridgeProviderEligibilityRequest {
  const state = args.state.trim().toUpperCase()
  const date = args.dateOfService ?? new Date()
  return {
    payerId: args.payerId,
    serviceTypeId: args.serviceTypeId.trim(),
    location: { state },
    dateOfService: date.toISOString(),
  }
}

export function parseSoftEligibleProviders(
  response: BridgeProviderEligibilityResponse,
): BridgeSoftEligibleProvider[] {
  if (!Array.isArray(response.providers)) {
    return []
  }
  return response.providers.filter(
    (p): p is BridgeSoftEligibleProvider =>
      typeof p === 'object' &&
      p !== null &&
      typeof (p as BridgeSoftEligibleProvider).id === 'string' &&
      typeof (p as BridgeSoftEligibleProvider).name === 'string',
  )
}

export function isBridgeSoftEligible(response: BridgeProviderEligibilityResponse): boolean {
  // Bridge soft-eligibility: status ELIGIBLE means payer/state is contracted;
  // providers may still be empty. Bookable only when both ELIGIBLE and ≥1 provider.
  // https://docs.usebridge.com/documentation/eligibility/soft-eligibility.md
  if (response.status?.toUpperCase() !== 'ELIGIBLE') {
    return false
  }
  return parseSoftEligibleProviders(response).length > 0
}
